import { getAuthToken } from '../hooks/useAuth';

export type BlobItem = {
  name: string;
  size: number;
  contentType: string;
  lastModified: string;
  url: string;
};

async function authHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listBlobs(userId: string): Promise<BlobItem[]> {
  const baseUrl = process.env.REACT_APP_GET_FILES_URL!;
  const separator = baseUrl.includes('?') ? '&' : '?';
  const url = `${baseUrl}${separator}userId=${encodeURIComponent(userId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: await authHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to list files');
  }

  const data = await response.json();

  // Handle both formats: array directly or wrapped in object
  return Array.isArray(data) ? data : (data.blobs || []);
}

export async function deleteBlob(filename: string, userId: string): Promise<void> {
  const url = process.env.REACT_APP_DELETE_USER_FILE_URL;

  if (!url) {
    throw new Error('Delete file URL not configured');
  }

  if (!userId) {
    throw new Error('User ID is required');
  }

  const response = await fetch(`${url}&filename=${encodeURIComponent(filename)}&userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });

  // Treat both 200 (success) and 404 (doesn't exist) as success
  if (response.ok || response.status === 404) {
    return; // Success - file is gone either way
  }

  throw new Error(`Failed to delete file: ${response.status}`);
}

export async function deleteFolder(folderPath: string, allBlobs: BlobItem[], userId: string): Promise<void> {
  const blobsInFolder = allBlobs.filter(blob =>
    blob.name.startsWith(folderPath + '/')
  );

  // Delete all files in the folder, but continue even if some fail
  const deletePromises = blobsInFolder.map(async (blob) => {
    try {
      await deleteBlob(blob.name, userId);
      console.log(`Deleted: ${blob.name}`);
    } catch (error) {
      // Log but don't fail - file might already be deleted
      console.warn(`Could not delete ${blob.name}:`, error);
    }
  });

  await Promise.all(deletePromises);

  // If no files were found or all deletes failed, still return success
  // since the folder effectively doesn't exist anymore
}

export async function renameBlob(oldName: string, newName: string, userId: string): Promise<void> {
  const url = process.env.REACT_APP_RENAME_USER_FILE_URL;

  if (!url) {
    throw new Error('Rename file URL not configured');
  }

  if (!userId) {
    throw new Error('User ID is required');
  }

  const response = await fetch(`${url}&oldName=${encodeURIComponent(oldName)}&newName=${encodeURIComponent(newName)}&userId=${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: await authHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to rename file');
  }
}

export async function getBlobStats(userId: string): Promise<{
  totalFiles: number;
  totalSize: number;
  filesThisWeek: number;
  filesByType: { [key: string]: number };
}> {
  const blobs = await listBlobs(userId);

  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const stats = {
    totalFiles: 0,
    totalSize: 0,
    filesThisWeek: 0,
    filesByType: {} as { [key: string]: number }
  };

  blobs.forEach(blob => {
    if (blob.name.endsWith('/.keep')) return;

    stats.totalFiles++;
    stats.totalSize += blob.size;

    const fileDate = new Date(blob.lastModified);
    if (fileDate >= oneWeekAgo) {
      stats.filesThisWeek++;
    }

    const type = blob.contentType.split('/')[0] || 'other';
    stats.filesByType[type] = (stats.filesByType[type] || 0) + 1;
  });

  return stats;
}

export async function generateShareLink(
  filename: string,
  userId: string,
  hours: number = 24
): Promise<{
  shareUrl: string;
  expiresAt: string;
  expiresIn: string;
}> {
  const url = process.env.REACT_APP_GENERATE_SHARE_LINK_URL;

  if (!url) {
    throw new Error('Share link URL not configured');
  }

  if (!userId) {
    throw new Error('User ID is required');
  }

  const response = await fetch(
    `${url}&filename=${encodeURIComponent(filename)}&userId=${encodeURIComponent(userId)}&hours=${hours}`,
    { headers: await authHeaders() }
  );

  if (!response.ok) {
    throw new Error('Failed to generate share link');
  }

  return await response.json();
}
